import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function subscriptionToJSON(sub) {
  if (!sub) return null;
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    keys: json.keys || {},
  };
}

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export default function usePushNotifications(token) {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported] = useState(isPushSupported());

  // Check existing subscription on mount
  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setIsSubscribed(!!sub);
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    if (!token) return { ok: false, reason: "no-auth" };
    setLoading(true);
    try {
      // 1. Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        return { ok: false, reason: "permission-denied" };
      }

      // 2. Fetch VAPID public key
      const { data: keyResp } = await axios.get(`${API}/push/vapid-public-key`);
      const applicationServerKey = urlBase64ToUint8Array(keyResp.public_key);

      // 3. Subscribe via PushManager
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      // 4. Send subscription to backend
      await axios.post(
        `${API}/push/subscribe`,
        subscriptionToJSON(sub),
        { headers: getAuthHeaders(token) }
      );

      setIsSubscribed(true);
      return { ok: true };
    } catch (e) {
      console.error("Push subscribe failed:", e);
      return { ok: false, reason: "error", error: e };
    } finally {
      setLoading(false);
    }
  }, [supported, token]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return { ok: false };
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const subJSON = subscriptionToJSON(sub);
        await sub.unsubscribe();
        try {
          await axios.post(
            `${API}/push/unsubscribe`,
            subJSON,
            { headers: getAuthHeaders(token) }
          );
        } catch (e) {
          // ignore - browser unsubscribed locally already
        }
      }
      setIsSubscribed(false);
      return { ok: true };
    } catch (e) {
      console.error("Push unsubscribe failed:", e);
      return { ok: false, error: e };
    } finally {
      setLoading(false);
    }
  }, [supported, token]);

  const sendTest = useCallback(async () => {
    if (!token) return { ok: false };
    try {
      const { data } = await axios.post(
        `${API}/push/test`,
        {},
        { headers: getAuthHeaders(token) }
      );
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: e };
    }
  }, [token]);

  return {
    supported,
    permission,
    isSubscribed,
    loading,
    subscribe,
    unsubscribe,
    sendTest,
  };
}
